import { useSelector } from 'react-redux';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IDispatcherType } from 'types';

import { GetLanguageConfig, getDefaultLanguageConfig } from 'utils/LanguageUtils';
import { IAPI_ACTION } from 'types';
import Toasty from 'components/atoms/Toasty/Toasty';

interface IInitialState {
	loading: boolean;
	isFetched: boolean;
	error: string;
	data: unknown;
}

const INITIAL_STATE = (): IInitialState => ({
	loading: false,
	isFetched: false,
	error: '',
	data: {},
});

export const setAPIError = (state: ReturnType<typeof INITIAL_STATE>, action: IAPI_ACTION): void => {
	state.loading = false;
	state.error = action.error.message || 'Something went wrong';
	Toasty.error(state.error);
};

export const getLanguageConfig = createAsyncThunk('languageConfigSlice', GetLanguageConfig);

export const LanguageConfigSlice = createSlice({
	name: 'languageConfigSlice',

	initialState: {
		...INITIAL_STATE(),
		data: getDefaultLanguageConfig(),
	},

	reducers: {
		resetLanguageConfig: (state) => {
			state.data = getDefaultLanguageConfig();
		},
	},

	extraReducers: {
		[getLanguageConfig.pending.toString()]: (state: ReturnType<typeof INITIAL_STATE>) => {
			state.data = getDefaultLanguageConfig();
			state.error = '';
			state.loading = true;
			state.isFetched = false;
		},
		[getLanguageConfig.rejected.toString()]: setAPIError,
		[getLanguageConfig.fulfilled.toString()]: (state: ReturnType<typeof INITIAL_STATE>, action: IAPI_ACTION<any>) => {
			const { payload } = action;
			state.loading = false;
			state.isFetched = true;
			state.error = '';
			state.data = payload;
		},
	},
});

interface IStates {
	[LanguageConfigSlice.name]: ReturnType<typeof LanguageConfigSlice.reducer>;
}

export const languageConfigSelector = (state: IStates): any => {
	return { languageData: state.languageConfigSlice.data, ...state.languageConfigSlice, data: undefined } || {};
};

export const useLanguageConfig = (): any => useSelector(languageConfigSelector);

export const setLanguageConfig =
	(lang?: string) =>
	async (dispatch: IDispatcherType): Promise<void> => {
		await dispatch(getLanguageConfig(lang));
	};

export const resetLanguageConfig =
	() =>
	async (dispatch: IDispatcherType): Promise<void> => {
		dispatch(LanguageConfigSlice.actions.resetLanguageConfig());
	};
